import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Checklist, Evidence as RisaEvidence } from "risa-data-model";
import { controlToastState, SpinningLoader } from "risa-oasis-ui_v2";
import { getFileDownloadUrl } from "../../../../api/firebase/firestoreService";
import { makePostCallToCohereWithQuestionnaireFilled } from "../../../../api/postCall/makePostCallToCohereWithQuestionnaireFilled";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import PdfRender from "../../../../components/pdfRender/pdfRender";
import { RootState } from "../../../../redux/store/store";
import { logDataToConsole } from "../../../../utils/customLogger";
import { Question, QuestionAnswer } from "../../types";
import RecursiveQuestion from "../recursiveQuestion/recursiveQuestion";

interface FormQuestionsProps {
  enableSubmitButton: (value: boolean) => void;
  makePostCallForFormSubmission: boolean;
  docId: string;
  faxData: any;
  isLoading: (loading: boolean) => void;
  questionnaireStatus?: string | null;
  isQuestionnaireLoading?: boolean;
  onAnswersChange?: (answers: { id: string; answer: any }[]) => void;
}

// const convertToRisaEvidence = (evidence: Evidence): RisaEvidence => {
//   return evidence as unknown as RisaEvidence;
// };

const FormQuestions = (props: FormQuestionsProps) => {
  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );
  const { data: availityConfigData } = useSelector(
    (state: RootState) => state.availityServiceReviewConfiguration,
  );
  const navigate = useNavigate();

  const [url, setUrl] = React.useState<string[] | null>(null);
  const [questionsState, setQuestionsState] = React.useState<Question[]>([]);
  const [allAnswers, setAllAnswers] = React.useState<
    { id: string; answer: any }[]
  >([]);

  useEffect(() => {
    if (props.faxData) {
      const questions = props.faxData?.question_tree as unknown as Question[];
      setQuestionsState(questions);
    }
  }, [props.faxData]);

  const handleNewAnswerChange = (questionId: string, answer: any) => {
    setAllAnswers((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === questionId);
      if (existingIndex >= 0) {
        const newAnswers = [...prev];
        newAnswers[existingIndex] = { id: questionId, answer: answer };
        return newAnswers;
      } else {
        return [...prev, { id: questionId, answer: answer }];
      }
    });
  };

  useEffect(() => {
    if (props.onAnswersChange) {
      props.onAnswersChange(allAnswers);
    }
  }, [allAnswers, props.onAnswersChange]);

  const checkIfAllQuestionsAnswered = (questions: Question[]): boolean => {
    return questions.every((question) => {
      if (!question.answer) {
        return false;
      }
      if (question.children) {
        const childQuestions = Object.values(question.children).flat();
        return checkIfAllQuestionsAnswered(childQuestions);
      }
      return true;
    });
  };

  const convertQuestionToChecklist = (question: Question): Checklist => {
    const values = question.options.map((option) => ({
      id: option.id,
      statement: option.text,
      response:
        option.id === (question.answer as QuestionAnswer[])[0].id ? "true" : "",
      evidences: {
        positive: "",
        negative: "",
      },
    }));

    const evidence: RisaEvidence = {
      positive: question.facts?.supportive_facts?.join("\n") || "",
      negative: question.facts?.contradictory_facts?.join("\n") || "",
    };

    return {
      id: question.id,
      statement: question.question,
      values,
      evidences: evidence,
    };
  };

  const makePostRequestToCohere = async () => {
    props.isLoading(true);

    const checklistData = questionsState.map(convertQuestionToChecklist);

    makePostCallToCohereWithQuestionnaireFilled(
      props.docId ?? "",
      checklistData,
      availityConfigData ?? {},
    );
    await new Promise((resolve) => setTimeout(resolve, 5000));
    controlToastState("submission-initiated-success");
    navigate("/worklists/");
    props.isLoading(false);
  };

  const fetchDownloadUrl = async (refUrl: string) => {
    try {
      const downloadUrl = await getFileDownloadUrl(refUrl);
      setUrl((prev) => [...(prev || []), downloadUrl]);
    } catch (err) {
      logDataToConsole("Error fetching download url", err);
    }
  };

  useEffect(() => {
    const allQuestionsAnswered = checkIfAllQuestionsAnswered(questionsState);
    props.enableSubmitButton(allQuestionsAnswered);
  }, [questionsState]);

  useEffect(() => {
    // Enable submit button if there are any answers in the new system
    props.enableSubmitButton(allAnswers.length > 0);
  }, [allAnswers, props]);

  useEffect(() => {
    fetchDownloadUrl(singleMedicalPaOrder?.clinical_document?.file_path ?? "");
  }, []);

  useEffect(() => {
    if (props.makePostCallForFormSubmission) {
      makePostRequestToCohere();
    }
  }, [props.makePostCallForFormSubmission]);

  return (
    <div className="form-questions--body">
      <div className="pharma-questionaire-form__body-content flex flex-1 overflow-hidden rounded-t-lg border bg-[#F7F9FA]">
        <div className="pharma-questionaire-form__body-content--left ml-4 mr-4 mt-4 flex-1 overflow-auto">
          {url && url.length > 0 ? (
            url.map((urlItem, index) => (
              <PdfRender key={index} file={urlItem} />
            ))
          ) : (
            <LoaderMessage message="Fetching documents..." />
          )}
        </div>

        <div className="pharma-questionaire-form__body-content--right flex flex-1 flex-col gap-2 overflow-auto border-l bg-white p-4">
          {props.isQuestionnaireLoading ? (
            <div className="flex h-full w-full items-center justify-center">
              <LoaderMessage message="Loading questionnaire..." />
            </div>
          ) : props.questionnaireStatus === "in_progress" ? (
            <div className="flex h-full w-full items-center justify-center gap-2">
              <div className="text-center">
                <SpinningLoader />
                <div className="font-semibold text-primaryGray-2">
                  Please wait...
                </div>
                <div className="text-small text-primaryGray-3">
                  The questionnaire is being answered
                </div>
              </div>
            </div>
          ) : (
            <>
              {(props.faxData?.question_tree || [])?.map(
                (question: any, index: number) => (
                  <React.Fragment key={question.id || index}>
                    <RecursiveQuestion
                      question={question}
                      onAnswerChange={handleNewAnswerChange}
                      currentAnswers={allAnswers}
                      questionNumber={`${index + 1}`}
                    />
                    {index !==
                      (props.faxData?.question_tree || []).length - 1 && (
                      <div className="form-questions--seperator"></div>
                    )}
                  </React.Fragment>
                ),
              )}

              {!props.faxData?.question_tree && (
                <div className="flex h-full items-center justify-center p-8">
                  <div className="text-center">
                    <p className="text-lg font-medium text-primaryGray-2">
                      No questions available
                    </p>
                    <p className="text-sm text-primaryGray-4">
                      Please check if the questionnaire data exists for this
                      order.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormQuestions;
